import type { View } from '../router/router';
import { apiClient } from '../services/api-client';
import { toast } from '../components/layout/toast';
import { renderWorkoutSessionCard, type WorkoutSession } from '../utils/workout-formatter';
import { getTyrisIcon } from '../components/ui/tyris-icon';

export class WorkoutHistoryPage implements View {
  private sessions: WorkoutSession[] = [];
  private currentPage = 1;
  private itemsPerPage = 5;
  private loading = true;

  async render(): Promise<string> {
    if (this.loading) {
      try {
        this.sessions = await apiClient.get<WorkoutSession[]>('/api/workout-sessions/my-history');
      } catch (err: any) {
        toast.error('Error al cargar el historial de entrenamientos.');
      } finally {
        this.loading = false;
      }
    }

    if (this.sessions.length === 0) {
      return `
        <div class="dashboard-header"><div class="dashboard-title">
          <h2 style="font-size:1.6rem; font-weight:800;">Historial de Entrenamientos</h2>
          <p style="color:var(--text-muted);">Todas tus sesiones registradas.</p>
        </div></div>
        <div class="card-glass card-workout" style="text-align:center;padding:4rem 2rem;margin-top:2rem;">
          <div style="margin-bottom:1rem; color:var(--tyris-energy);">
            ${getTyrisIcon('comenzar-entrenamiento', { size: 56 })}
          </div>
          <h3 style="font-size:1.5rem;margin-bottom:0.75rem;color:var(--tyris-energy);">Sin Sesiones</h3>
          <p style="color:var(--text-muted);margin-bottom:2rem;">Aún no has completado ningún entrenamiento.</p>
          <a href="#/workout" class="btn btn-student" style="width:auto;padding:0.75rem 2rem;">
            ${getTyrisIcon('comenzar-entrenamiento', { size: 20 })}
            <span>Comenzar Entrenamiento</span>
          </a>
        </div>
      `;
    }

    const totalPages = Math.ceil(this.sessions.length / this.itemsPerPage) || 1;
    if (this.currentPage > totalPages) this.currentPage = totalPages;
    if (this.currentPage < 1) this.currentPage = 1;

    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    const paginatedSessions = this.sessions.slice(startIndex, endIndex);

    const paginationHtml = this.sessions.length > this.itemsPerPage
      ? `
        <div style="display:flex;justify-content:center;align-items:center;gap:1.5rem;margin-top:2rem;padding:0.5rem 0;">
          <button id="prev-page" class="btn btn-outline" ${this.currentPage === 1 ? 'disabled' : ''} style="width:auto;padding:0.5rem 1.25rem;font-size:0.85rem;display:flex;align-items:center;gap:0.35rem;">
            ← Anterior
          </button>
          <span style="font-size:0.9rem;color:var(--text-muted);user-select:none;">
            Página <strong>${this.currentPage}</strong> de <strong>${totalPages}</strong>
          </span>
          <button id="next-page" class="btn btn-outline" ${this.currentPage === totalPages ? 'disabled' : ''} style="width:auto;padding:0.5rem 1.25rem;font-size:0.85rem;display:flex;align-items:center;gap:0.35rem;">
            Siguiente →
          </button>
        </div>
      `
      : '';

    return `
      <div class="dashboard-header" style="margin-bottom:1.5rem;">
        <div class="dashboard-title">
          <h2 style="font-size:1.6rem; font-weight:800;">Historial de Entrenamientos</h2>
          <p style="color:var(--text-muted);">${this.sessions.length} sesión(es) registrada(s).</p>
        </div>
        <a href="#/workout" class="btn btn-student" style="width:auto;padding:0.6rem 1.25rem;">
          ${getTyrisIcon('comenzar-entrenamiento', { size: 18 })}
          <span>Nueva Sesión</span>
        </a>
      </div>

      <div style="display:flex;flex-direction:column;gap:1.25rem;">
        ${paginatedSessions.map(session => renderWorkoutSessionCard(session)).join('')}
      </div>

      ${paginationHtml}
    `;
  }

  afterRender(): void {
    const renderInline = async () => {
      const viewArea = document.getElementById('app-view-area');
      if (viewArea) {
        viewArea.innerHTML = await this.render();
        this.afterRender();
      }
    };

    const prevBtn = document.getElementById('prev-page');
    prevBtn?.addEventListener('click', () => {
      if (this.currentPage > 1) {
        this.currentPage--;
        renderInline();
      }
    });

    const nextBtn = document.getElementById('next-page');
    nextBtn?.addEventListener('click', () => {
      const totalPages = Math.ceil(this.sessions.length / this.itemsPerPage);
      if (this.currentPage < totalPages) {
        this.currentPage++;
        renderInline();
      }
    });
  }
}
